import { datadogRum } from '@datadog/browser-rum';
import { Inject, Injectable } from '@angular/core';
import { PerformanceInfoProvider } from '../performance-info-provider/performance-info-provider';
import { DataDogConfiguration } from '../../models';

class MemoryError extends Error {
  constructor(usedMemoryInMegaBytes: number) {
    super(`Anomaly detected. Memory usage is ${Math.round(usedMemoryInMegaBytes)}.`);
  }
}

const memoryTrackingPeriod = 10000;

@Injectable()
export class MemoryTracker {
  private maxMegaBytes: number;
  private memoryTrackingInterval: number | null = null;

  constructor(@Inject('dd-config') private ddConfig: DataDogConfiguration, private performanceInfoProvider: PerformanceInfoProvider) { }

  startMemoryTracking(): void {
    // Clean up any existing interval
    this.stopMemoryTracking();

    const maxMegabytesFromConfig = this.ddConfig.maxAllowedMemoryUsageMb as number;

    if (typeof maxMegabytesFromConfig === 'number' && maxMegabytesFromConfig > 0) {
      this.maxMegaBytes = maxMegabytesFromConfig;
      const memoryInfo = this.performanceInfoProvider.getMemoryInfo();

      if (memoryInfo) {
        this.memoryTrackingInterval = window.setInterval(() => {
          try {
            const currentMemoryInfo = this.performanceInfoProvider.getMemoryInfo();
            const usedMemory = currentMemoryInfo?.usedJSHeapSize;

            if (typeof usedMemory === 'number' && usedMemory >= this.maxMegaBytes) {
              this.trackMemoryError(usedMemory);
            }
          } catch (error) {
            this.stopMemoryTracking();
          }
        }, memoryTrackingPeriod);
      }
    }
  }

  private trackMemoryError(usedMemoryInMegaBytes: number): void {
    datadogRum.addError(new MemoryError(usedMemoryInMegaBytes));
  }

  // Add cleanup method
  private stopMemoryTracking(): void {
    if (this.memoryTrackingInterval) {
      clearInterval(this.memoryTrackingInterval);
      this.memoryTrackingInterval = null;
    }
  }
}
